import { OktaAuth } from '@okta/okta-auth-js';
import Singleton from '../Singleton';
import {
  createCookie,
  expireCookie,
  getCookie
} from '../_shared/utils/cookies';
import { COOKIES } from '../_shared/constants/cookies';
import OKTA_CONFIG, {
  accessTokenStructurePlaceholder,
  refreshTokenStructurePlaceholder
} from '../_shared/constants/oktaConfig';
import ROUTES from '../components/Routes/constants';
import { sessionStorageService } from './StorageService';

export default class OktaAuthManagementService extends Singleton<
  OktaAuthManagementService
> {
  private oktaAuthClient: OktaAuth | null | undefined;

  private oktaAuthStarted = false;

  getOktaAuthClient() {
    if (this.oktaAuthClient != null) {
      return this.oktaAuthClient;
    }

    this.oktaAuthClient = new OktaAuth(OKTA_CONFIG.authorize);
    this.oktaAuthClient.tokenManager.on('renewed', (key, newToken) => {
      if (key === 'accessToken') {
        this.oktaAuthClient.tokenManager
          .get('refreshToken')
          .then(refreshToken => {
            this.saveTR5OktaRefreshToken(refreshToken.refreshToken);
          });
      }
    });

    this.oktaAuthClient.tokenManager.on('expired', (key, newToken) => {
      if (key === 'accessToken') {
        const tr4RefreshToken = getCookie(COOKIES.TR4_OKTA_REFRESH_TOKEN);
        if (tr4RefreshToken) {
          this.refreshUsingTR4RefreshToken(
            this.oktaAuthClient,
            tr4RefreshToken
          ).catch(() => {
            this.refreshUsingOktaRedirect(this.oktaAuthClient);
          });
        }
      }
    });

    return this.oktaAuthClient;
  }

  initiateOktaSession(fromOktaRedirect: boolean = false): Promise<void> {
    const oktaClient = this.getOktaAuthClient();

    const tr4RefreshToken = getCookie(COOKIES.TR4_OKTA_REFRESH_TOKEN);
    if (tr4RefreshToken) {
      sessionStorageService.removeItem(OKTA_CONFIG.oktaTokenStorage);
    }

    const { hash } = window.location;
    if (hash.includes('code=')) {
      return this.readOktaCodeFromUrl(oktaClient, hash);
    }

    if (tr4RefreshToken) {
      return this.refreshUsingTR4RefreshToken(oktaClient, tr4RefreshToken)
        .then(() => {
          this.startOktaClient(oktaClient);
        })
        .catch(() => {
          this.refreshUsingOktaRedirect(oktaClient);
        });
    }

    return oktaClient.tokenManager
      .get('accessToken')
      .then(token => {
        if (token && !oktaClient.tokenManager.hasExpired(token)) {
          if (!this.oktaAuthStarted) {
            this.startOktaClient(oktaClient);
          }

          // incase user is stuck or enters /okta-redirect url manually
          if (fromOktaRedirect) {
            window.location.replace(ROUTES.BUYING_ACTIVE);
          }
        } else {
          // Token has been removed due to expiration or error while renewing
          this.refreshUsingOktaRedirect(oktaClient);
        }
      })
      .catch(() => {
        this.refreshUsingOktaRedirect(oktaClient);
      });
  }

  logoutOktaSession = async () => {
    const oktaClient = this.getOktaAuthClient();
    await oktaClient.revokeAccessToken();

    const exists = await oktaClient.session.exists();
    if (exists) {
      await oktaClient.closeSession();
    }
  };

  private refreshUsingTR4RefreshToken = (
    oktaClient: OktaAuth,
    tr4RefreshToken: string
  ) => {
    expireCookie(COOKIES.TR4_OKTA_REFRESH_TOKEN);
    oktaClient.tokenManager.add('accessToken', accessTokenStructurePlaceholder);
    oktaClient.tokenManager.add('refreshToken', {
      refreshToken: tr4RefreshToken,
      ...refreshTokenStructurePlaceholder
    });

    return oktaClient.tokenManager.renew('accessToken');
  };

  private readOktaCodeFromUrl = (oktaClient: OktaAuth, hash: string) => {
    return oktaClient.token.parseFromUrl().then(tokens => {
      oktaClient.tokenManager.setTokens(tokens.tokens);
      expireCookie(COOKIES.TR4_OKTA_REFRESH_TOKEN);
      this.saveTR5OktaRefreshToken(tokens.tokens.refreshToken.refreshToken);
      this.startOktaClient(oktaClient);

      // redirect based on state decoding
      const { state } = this.getUrlParamsFromHash(hash);
      const { pathname, search } = JSON.parse(atob(state));
      window.location.replace(
        pathname ? pathname + search : ROUTES.BUYING_ACTIVE
      );
    });
  };

  private refreshUsingOktaRedirect = (oktaClient: OktaAuth) => {
    oktaClient.token.getWithRedirect({
      responseType: ['token', 'id_token'],
      scopes: OKTA_CONFIG.token.scopes,
      state: btoa(
        JSON.stringify({
          pathname: window.location.pathname,
          search: window.location.search
        })
      )
    });
  };

  private saveTR5OktaRefreshToken = (refreshToken: string) => {
    createCookie(COOKIES.TR5_OKTA_REFRESH_TOKEN, refreshToken);
  };

  private startOktaClient = (oktaClient: OktaAuth) => {
    this.oktaAuthStarted = true;
    oktaClient.start();
  };

  private getUrlParamsFromHash = (hash: string) => {
    const urlParams = {};

    const hashParams = hash.split('&');
    hashParams.forEach(hashParam => {
      const hashKey = hashParam.slice(0, hashParam.indexOf('='));
      urlParams[hashKey] = decodeURIComponent(
        hashParam.slice(hashParam.indexOf('=') + 1)
      );
    });

    return urlParams;
  };
}
